// AddressFields.tsx
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import MapAddressPicker from "./MapAddressPicker";

type Props = {
  form?: any; // now optional
  prefix?: string;
  requiredFlags?: [boolean, boolean, boolean, boolean, boolean];
  disabled?: boolean; // explicit override
  readOnly?: boolean; // NEW
};

export default function AddressFields({
  form,
  prefix = "",
  requiredFlags = [false, false, false, false, false],
  disabled = false,
  readOnly = false,
}: Props) {
  const [req1, req2, req3, req4, req5] = requiredFlags;

  const handleAddressSelect = (addr: {
    line1: string;
    line2: string;
    city: string;
    pincode: string;
    landmark: string;
    lat?: number;
    lng?: number;
  }) => {
    console.log("Address selected in AddressFields:", addr);
    if (form) {
      const options = { shouldValidate: true, shouldDirty: true };

      // Full address from map goes to line2
      const fullAddrFromMap = [addr.line1, addr.line2].filter(Boolean).join(", ");

      form.setValue(`${prefix}_address_line1`, "", options);
      form.setValue(`${prefix}_address_line2`, fullAddrFromMap, options);
      form.setValue(`${prefix}_address_line3`, addr.city, options);
      form.setValue(`${prefix}_address_pincode`, addr.pincode, options);
      form.setValue(`${prefix}_address_landmark`, addr.landmark, options);
      
      if (addr.lat) form.setValue(`${prefix}_address_lat`, String(addr.lat), options);
      if (addr.lng) form.setValue(`${prefix}_address_lng`, String(addr.lng), options);
    }
  };

  const fields = [
    { name: `${prefix}_address_line1`, label: "Flat / House no / Building name", required: req1 },
    { name: `${prefix}_address_line2`, label: "Address Line 2", required: req2 },
    { name: `${prefix}_address_line3`, label: "City / Town", required: req3 },
    { name: `${prefix}_address_landmark`, label: "Landmark", required: req4 },
    {
      name: `${prefix}_address_pincode`,
      label: "Pincode",
      required: req5,
      maxLength: 6,
      numeric: true,
    },
    { name: `${prefix}_address_lat`, label: "Latitude", disabled: true },
    { name: `${prefix}_address_lng`, label: "Longitude", disabled: true },
  ];

  const allDisabled = disabled || readOnly;

  // ---------------------------------------------------------
  // READ-ONLY OR NO-CONTROL MODE (NO react-hook-form Controller)
  // ---------------------------------------------------------
  if (readOnly || !form || !form.control) {
    return (
      <div className="space-y-3">
        {fields.map((f, index) => (
          <div key={f.name} className="flex flex-col space-y-1">
            {!readOnly && index === 0 && !allDisabled && (
              <div className="flex justify-center mb-3">
                <MapAddressPicker onAddressSelect={handleAddressSelect} />
              </div>
            )}
            <label className="text-sm font-medium">
              {f.label}
              {f.required ? <span className="text-red-500">*</span> : null}
            </label>
            <Input
              disabled={allDisabled || f.disabled}
              placeholder={f.label}
              maxLength={f.maxLength}
              inputMode={f.numeric ? "numeric" : "text"}
              className="h-9"
              value={form?.getValues(f.name) || ""}
              onChange={(e) => form?.setValue(f.name, e.target.value, { shouldValidate: true, shouldDirty: true })}
              showSuccess={!!(form?.getValues(f.name)) && !form?.formState?.errors?.[f.name]}
            />
          </div>
        ))}
      </div>
    );
  }

  // ---------------------------------------------------------
  // FORM MODE (react-hook-form)
  // ---------------------------------------------------------
  return (
    <div className="space-y-3">
      {fields.map((f, index) => (
        <FormField
          key={f.name}
          control={form.control}
          name={f.name}
          render={({ field }) => (
            <FormItem>
              {index === 0 && !allDisabled && (
                <div className="flex justify-center mb-3">
                  <MapAddressPicker onAddressSelect={handleAddressSelect} />
                </div>
              )}
              <div className="flex items-center justify-between">
                <FormLabel>
                  {f.label} {f.required ? <span className="text-red-500">*</span> : null}
                </FormLabel>
              </div>
              <FormControl>
                <Input
                  {...field}
                  disabled={allDisabled || f.disabled}
                  maxLength={f.maxLength}
                  inputMode={f.numeric ? "numeric" : "text"}
                  className="h-9"
                  showSuccess={!!field.value && !form.formState.errors[f.name as keyof typeof form.formState.errors]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </div>
  );
}
